import type { Ref } from 'vue'
import type { Extra } from '@/components/orders/ExtrasSelector.vue'
import type { ExtendedMenuItem, MenuItemVariant } from '@/types/order'
import type { OrderPerson, PersonOrderItem } from '@/composables/useMultipleDiners'
import { getEffectivePrice } from '@/utils/priceHelpers'

export interface AddItemFromModalPayload {
  item: ExtendedMenuItem
  variant: MenuItemVariant | null
  quantity: number
  notes: string
  unit_price?: number
  item_type?: 'dine_in' | 'takeaway'
  modifier_extras?: Extra[]
}

interface UseOrderItemModalAdderOptions {
  persons: Ref<OrderPerson[]>
  activePersonIndex: Ref<number>
  selectedItem: Ref<ExtendedMenuItem | null>
  showingItemOptions: Ref<boolean>
}

export function useOrderItemModalAdder(options: UseOrderItemModalAdderOptions) {
  const calculateUnitPrice = (payload: AddItemFromModalPayload) => {
    const { item, variant, unit_price } = payload

    let price = Number(unit_price ?? 0)

    if (!price || price <= 0) {
      if (variant) {
        if (variant.price && variant.price > 0) {
          price = getEffectivePrice(variant.price, variant.discount_price)
        } else {
          const basePrice = item.price || 0
          const adjustment = variant.price_adjustment || 0
          const variantPrice = basePrice + adjustment
          price = getEffectivePrice(variantPrice, variant.discount_price)
        }
      } else {
        price = getEffectivePrice(item.price || 0, item.discount_price)
      }
    }

    return price
  }

  const handleAddItemFromModal = (payload: AddItemFromModalPayload) => {
    const { item, variant, quantity, notes, item_type, modifier_extras } = payload
    const activePerson = options.persons.value[options.activePersonIndex.value]

    if (activePerson) {
      const price = calculateUnitPrice(payload)

      for (let i = 0; i < quantity; i++) {
        const createdItem: PersonOrderItem = {
          menu_item_id: Number(item.id),
          variant_id: variant ? Number(variant.id) : null,
          variant: variant
            ? {
                id: Number(variant.id),
                name: variant.name,
                price: variant.price,
                price_adjustment: variant.price_adjustment,
                discount_price: variant.discount_price
              }
            : null,
          quantity: 1,
          special_instructions: notes || '',
          unit_price: price,
          extras: modifier_extras || [],
          person_id: activePerson.id ?? null,
          item_type: item_type || 'dine_in'
        }

        activePerson.items.push(createdItem)
      }
    }

    options.selectedItem.value = null
    options.showingItemOptions.value = false
  }

  return {
    handleAddItemFromModal
  }
}
